import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class WooCommerceOrder extends ModelObject<WooCommerceOrder> {

    public version: number = 0;
    /**
    * The WooCommerce unique id for this order.
    */
    public wooCommerceOrderId: number = 0;
    /**
    * The date and time this order was created in Shopify.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);
    /**
    * The order number as it would appear in Shopify.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public orderNumber: string = "";
    /**
    * The total number of items in the order.
    */
    public totalOrderQuantity: number = 0;
    /**
    * The total net value of the order including delivery.
    */
    public totalNetPrice: number = 0;
    private _brewmanOrderId: Guid | null = null;
    /**
    * The Brewman order id assigned to this order on a successful import. If empty, check the error message field for details.
    */
    public get brewmanOrderId(): Guid | null { return this._brewmanOrderId }
    public set brewmanOrderId(value: Guid | null) {
      this._brewmanOrderId = value === undefined ? null : value;
    }
    /**
    * Error information associated with a failed import.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public errorMessage: string = "";
    /**
    * The store associated with this order.
    * Not Nullable
    * Preserves leading and trailing whitespace.
    */
    public storeUrl: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.wooCommerceOrderId = jsonSerializedObject.wooCommerceOrderId;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
          this.orderNumber = jsonSerializedObject.orderNumber;
          this.totalOrderQuantity = jsonSerializedObject.totalOrderQuantity;
          this.totalNetPrice = jsonSerializedObject.totalNetPrice;
          this.brewmanOrderId = jsonSerializedObject.brewmanOrderId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.brewmanOrderId);
          this.errorMessage = jsonSerializedObject.errorMessage;
          this.storeUrl = jsonSerializedObject.storeUrl;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.wooCommerceOrderId = this.wooCommerceOrderId;
        toRet.createdAt = this.createdAt.toString();
        toRet.orderNumber = this.orderNumber;
        toRet.totalOrderQuantity = this.totalOrderQuantity;
        toRet.totalNetPrice = this.totalNetPrice;
        toRet.brewmanOrderId = this.brewmanOrderId == null ? null : this.brewmanOrderId.toString();
        toRet.errorMessage = this.errorMessage;
        toRet.storeUrl = this.storeUrl;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "wooCommerceOrderId", rule: (v: any) => v == null || v >= -2147483648 || "WooCommerceOrderId is not greater than minimum allowed value: -2147483648"},
            {field: "wooCommerceOrderId", rule: (v: any) => v == null || v <= 2147483647 || "WooCommerceOrderId is above maximum allowed value: 2147483647"},
            {field: "brewmanOrderId", rule: (v: any) => v == null || !v.isEmpty() || "WooCommerceOrder.BrewmanOrderId cannot be empty"},
    ];
}


export class FilteredWooCommerceOrdersResult extends ModelObject<FilteredWooCommerceOrdersResult> {

    public matchingCount: number = 0;
    public wooCommerceOrders: (WooCommerceOrder)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.wooCommerceOrders = jsonSerializedObject.wooCommerceOrders.map((v: any) => new WooCommerceOrder(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.wooCommerceOrders = this.wooCommerceOrders.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "wooCommerceOrders", rule: (v: any) => !!v || "wooCommerceOrders is required"},
            {field: "wooCommerceOrders", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as WooCommerceOrder[]).reduce((acc: Array<string>, curr: WooCommerceOrder) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class WooCommerceOrderFilter extends ModelObject<WooCommerceOrderFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _createdFromDate: LocalDate | null = null;
    /**
    * Minimum (inclusive) created at date
    */
    public get createdFromDate(): LocalDate | null { return this._createdFromDate }
    public set createdFromDate(value: LocalDate | null) {
      this._createdFromDate = value === undefined ? null : value;
    }
    private _createdToDate: LocalDate | null = null;
    /**
    * Maximum (inclusive) created at date
    */
    public get createdToDate(): LocalDate | null { return this._createdToDate }
    public set createdToDate(value: LocalDate | null) {
      this._createdToDate = value === undefined ? null : value;
    }
    private _storeUrls: readonly (string)[] | null = null;
    /**
    * Limit to specified store urls
    */
    public get storeUrls(): readonly (string)[] | null { return this._storeUrls }
    public set storeUrls(value: readonly (string)[] | null) {
      this._storeUrls = value === undefined ? null : value;
    }
    private _wooCommerceOrderIdentifiers: readonly (number)[] | null = null;
    /**
    * Limit to just orders with one of the given woo-commerce-allocated ids.
    */
    public get wooCommerceOrderIdentifiers(): readonly (number)[] | null { return this._wooCommerceOrderIdentifiers }
    public set wooCommerceOrderIdentifiers(value: readonly (number)[] | null) {
      this._wooCommerceOrderIdentifiers = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.createdFromDate = jsonSerializedObject.createdFromDate == null ? null : LocalDate.parse(jsonSerializedObject.createdFromDate);
          this.createdToDate = jsonSerializedObject.createdToDate == null ? null : LocalDate.parse(jsonSerializedObject.createdToDate);
          this.storeUrls = jsonSerializedObject.storeUrls == null ? null : jsonSerializedObject.storeUrls.map((v: any) => v);
          this.wooCommerceOrderIdentifiers = jsonSerializedObject.wooCommerceOrderIdentifiers == null ? null : jsonSerializedObject.wooCommerceOrderIdentifiers.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.createdFromDate = this.createdFromDate == null ? null : this.createdFromDate.toString();
        toRet.createdToDate = this.createdToDate == null ? null : this.createdToDate.toString();
        toRet.storeUrls = this.storeUrls == null ? null : this.storeUrls.map(v => v);
        toRet.wooCommerceOrderIdentifiers = this.wooCommerceOrderIdentifiers == null ? null : this.wooCommerceOrderIdentifiers.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class WooCommerceOrderService extends ServiceBase {

  public async getWooCommerceOrdersByFilter(filter: WooCommerceOrderFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredWooCommerceOrdersResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredWooCommerceOrdersResult(response.results);;
    return this.makeJsonRequest("wooCommerceOrder/v1/getWooCommerceOrdersByFilter", req, false, extractor, opts);
  };
}

export const wooCommerceOrderService = new WooCommerceOrderService();